import React, { useCallback, useEffect, useRef } from 'react';
import Slider from 'rc-slider';
import TextInput from './TextInput';
import Flex, { FlexDirection, FlexJustification } from './Flex';
import { Range, SliderInputValue } from '../../models';
import { countDecimals, round } from '../../services';
import { InnoRange } from '@innomotics/innomotics-build-react-lib';

interface SliderInputProps {
    label: string;
    unit: string;
    range: Range;
    value: SliderInputValue;
    name: string;
    dark?: boolean;
    disabled?: boolean;
    onInputChange?: (value: string, name: string) => void;
    onSliderChange?: (value: number, name: string) => void;
    onInputBlur?: (value: number, name: string) => void;
    onSliderBlur?: (value: number, name: string) => void;
}

const SliderInput = ({ label, unit, range, value, disabled, name, onInputChange, onSliderChange, onInputBlur, onSliderBlur }: SliderInputProps) => {
    const correctedMin = range.min === range.max ? round(range.min - range.step, countDecimals(range.step)) : range.min;
    const rangeRef = useRef<any>(null);
    const isInteractingRef = useRef(false);
    const currentValueRef = useRef<number>(value.sliderValue);

    useEffect(() => {
        currentValueRef.current = value.sliderValue;
    }, [value.sliderValue]);

    useEffect(() => {
        const rangeElement = rangeRef.current;
        
        if (rangeElement) {
            const handleMouseDown = () => {
                isInteractingRef.current = true;
            };

            const handleMouseUp = () => {
                if (isInteractingRef.current && onSliderBlur) {
                    onSliderBlur(currentValueRef.current, name);
                    isInteractingRef.current = false;
                }
            };

            const handleTouchEnd = () => {
                if (isInteractingRef.current && onSliderBlur) {
                    onSliderBlur(currentValueRef.current, name);
                    isInteractingRef.current = false;
                }
            };

            rangeElement.addEventListener('mousedown', handleMouseDown);
            rangeElement.addEventListener('mouseup', handleMouseUp);
            rangeElement.addEventListener('touchend', handleTouchEnd);
            document.addEventListener('mouseup', handleMouseUp);

            return () => {
                rangeElement.removeEventListener('mousedown', handleMouseDown);
                rangeElement.removeEventListener('mouseup', handleMouseUp);
                rangeElement.removeEventListener('touchend', handleTouchEnd);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [name, onSliderBlur]);

    const handleSliderValueChanged = useCallback((event: CustomEvent) => {
        const newValue = Number(event.detail.value);
        
        if (isNaN(newValue)) return;

        currentValueRef.current = newValue;

        if (onSliderChange) {
            onSliderChange(newValue, name);
        }
    }, [name, onSliderChange]);

    return (
        <div className='slider-input-container'>
            <div className='slider-title'>{label}</div>
            <Flex direction={FlexDirection.Row} justification={FlexJustification.Center} style={{ alignItems: 'center' }}>
                <div>{unit}</div>
                <InnoRange
                    ref={rangeRef}
                    step={range.step}
                    min={correctedMin}
                    max={range.max}
                    value={value.sliderValue}
                    disabled={disabled}
                    onValueChanged={handleSliderValueChanged}
                    style={{ margin: '0 20px' }}
                    showRuler={false}
                />
                <TextInput
                    label={''}
                    disabled={disabled}
                    onChange={(value, name) => onInputChange && !isNaN(Number(value)) && onInputChange(value, name)}
                    onBlur={x => onInputBlur && onInputBlur(Number(x.target.value), name)}
                    name={name ?? ''}
                    value={value.inputValue}
                    style={{ width: '120px' }}
                />
            </Flex>
        </div>
    );
};

export default SliderInput;
